/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { TFunction } from 'i18next';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomToggleButton, CustomTooltip } from '@opengeoweb/shared';
import { uiActions, uiSelectors, uiTypes } from '@opengeoweb/store';
import { TimeSeriesSelect } from '@opengeoweb/theme';
import type { TimeSeriesRootState } from '../../store/types';

export interface TimeSeriesSelectButtonConnectProps {
  isEnabled: boolean;
  onClick: () => void;
  t: TFunction;
  plotId: string;
  selectPlotId: string;
  panelId: string;
}

export const TimeSeriesSelectButtonConnect: React.FC<
  TimeSeriesSelectButtonConnectProps
> = ({ isEnabled, onClick, t, plotId, selectPlotId, panelId }) => {
  const dispatch = useDispatch();
  const currentSourcePanelId = useSelector((store: TimeSeriesRootState) =>
    uiSelectors.getSourcePanelId(store, uiTypes.DialogTypes.TimeSeriesSelect),
  );

  const isOpenInStore = useSelector((store: TimeSeriesRootState) =>
    uiSelectors.getisDialogOpen(store, uiTypes.DialogTypes.TimeSeriesSelect),
  );

  const handleClick = (): void => {
    onClick();

    const openDialogForDifferentPanel = currentSourcePanelId !== panelId;
    const dialogIsNotOpenForCurrentPanel =
      openDialogForDifferentPanel || !isOpenInStore;

    if (dialogIsNotOpenForCurrentPanel) {
      dispatch(
        uiActions.setToggleOpenDialog({
          type: uiTypes.DialogTypes.TimeSeriesSelect,
          setOpen: true,
          sourcePanelId: panelId,
        }),
      );
    }
  };
  const isSelectOpenForCurrentPlot = plotId === selectPlotId;
  const isOpenForPanel = isOpenInStore && currentSourcePanelId === panelId;

  return (
    <CustomTooltip title={t('timeseries-add-param-to-plot')}>
      <CustomToggleButton
        data-testid="timeSeriesSelectButton"
        disabled={!isEnabled}
        onClick={handleClick}
        selected={!!isOpenForPanel && isSelectOpenForCurrentPlot}
        sx={{
          marginLeft: '4px',
          height: '24px!important',
          width: '24px',
          paddingLeft: '5px',
          svg: {
            marginLeft: '8px',
          },
        }}
        variant="tool"
      >
        <TimeSeriesSelect />
      </CustomToggleButton>
    </CustomTooltip>
  );
};
